// Bruits de pas, synthétisés avec Web Audio : quelques souffles et coups très courts, filtrés selon le sol, un peu
// différents à chaque pas. Joués par le jeu (chaque pas du joueur) et par l'administration (essai dans l'éditeur d'éléments).
import type { Footstep } from '../../shared/footsteps.ts';

/** Un souffle (bruit blanc filtré) ; `sweep` : le filtre glisse vers cette fréquence (éclaboussure). */
interface Burst { at: number; duration: number; volume: number; filter: BiquadFilterType; freq: number; q?: number; sweep?: number; attack?: number }
/** Un coup sourd (son pur qui retombe) : le talon sur le bois. */
interface Thump { at: number; duration: number; volume: number; freq: number; to: number }

const burst = (at: number, duration: number, volume: number, filter: BiquadFilterType, freq: number, extra: Partial<Burst> = {}): Burst =>
  ({ at, duration, volume, filter, freq, ...extra });
/** `count` petits grains répartis sur `span` secondes (neige qui crisse, gravier). */
const grains = (count: number, span: number, volume: number, filter: BiquadFilterType, freq: number): Burst[] =>
  Array.from({ length: count }, (_, i) => burst((i / count) * span + Math.random() * 0.008, 0.018, volume * (0.7 + Math.random() * 0.5), filter, freq, { q: 1.5 }));

const STEPS: Readonly<Record<Footstep, () => { bursts: Burst[]; thumps: Thump[] }>> = {
  grass: () => ({ bursts: [burst(0, 0.05, 0.05, 'bandpass', 2600, { q: 0.8 }), burst(0.03, 0.06, 0.035, 'bandpass', 3400, { q: 0.8 })], thumps: [] }),
  stone: () => ({ bursts: [burst(0, 0.03, 0.045, 'highpass', 2200)], thumps: [{ at: 0, duration: 0.04, volume: 0.05, freq: 190, to: 140 }] }),
  wood: () => ({ bursts: [burst(0, 0.04, 0.03, 'bandpass', 900, { q: 1.2 })], thumps: [{ at: 0, duration: 0.08, volume: 0.12, freq: 125, to: 85 }] }),
  sand: () => ({ bursts: [burst(0, 0.09, 0.06, 'lowpass', 1300, { attack: 0.015 })], thumps: [] }),
  snow: () => ({ bursts: grains(5, 0.08, 0.045, 'bandpass', 1900), thumps: [] }),
  gravel: () => ({ bursts: grains(4, 0.06, 0.04, 'highpass', 3000), thumps: [{ at: 0, duration: 0.03, volume: 0.03, freq: 160, to: 120 }] }),
  water: () => ({ bursts: [burst(0, 0.14, 0.06, 'lowpass', 3200, { sweep: 450, attack: 0.01 })], thumps: [] }),
  carpet: () => ({ bursts: [burst(0, 0.06, 0.05, 'lowpass', 520, { attack: 0.01 })], thumps: [{ at: 0, duration: 0.05, volume: 0.04, freq: 95, to: 70 }] }),
};

/** Une seconde de bruit blanc par contexte audio, réutilisée par tous les pas. */
const noises = new WeakMap<BaseAudioContext, AudioBuffer>();
function noiseOf(ctx: BaseAudioContext): AudioBuffer {
  let buffer = noises.get(ctx);
  if (!buffer) {
    buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let i = 0; i < samples.length; i++) samples[i] = Math.random() * 2 - 1;
    noises.set(ctx, buffer);
  }
  return buffer;
}

/** Joue un pas sur ce sol, vers `destination`. */
export function playFootstep(ctx: AudioContext, destination: AudioNode, kind: Footstep): void {
  const { bursts, thumps } = STEPS[kind]();
  // Chaque pas un peu différent : hauteur et force.
  const pitch = 0.85 + Math.random() * 0.3;
  const force = 0.8 + Math.random() * 0.3;
  const start = ctx.currentTime + 0.005;
  for (const b of bursts) {
    const t0 = start + b.at, t1 = t0 + b.duration;
    const source = ctx.createBufferSource();
    source.buffer = noiseOf(ctx);
    const filter = ctx.createBiquadFilter();
    filter.type = b.filter;
    filter.frequency.setValueAtTime(b.freq * pitch, t0);
    if (b.sweep) filter.frequency.exponentialRampToValueAtTime(b.sweep * pitch, t1);
    if (b.q) filter.Q.value = b.q;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(b.volume * force, t0 + (b.attack ?? 0.003));
    gain.gain.exponentialRampToValueAtTime(0.0001, t1);
    source.connect(filter).connect(gain).connect(destination);
    // Un morceau pris au hasard dans la seconde de bruit.
    source.start(t0, Math.random() * 0.8);
    source.stop(t1 + 0.01);
  }
  for (const t of thumps) {
    const t0 = start + t.at, t1 = t0 + t.duration;
    const oscillator = ctx.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(t.freq * pitch, t0);
    oscillator.frequency.exponentialRampToValueAtTime(t.to * pitch, t1);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(t.volume * force, t0 + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, t1);
    oscillator.connect(gain).connect(destination);
    oscillator.start(t0);
    oscillator.stop(t1 + 0.01);
  }
}
